import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useRef,
  useMemo,
} from "react";
import { useApi } from "../hooks/callApi/callApi";

interface AvatarCache {
  [email: string]: {
    avatarUrl: string | undefined;
    timestamp: number;
    loading: boolean;
  };
}

interface AvatarCacheContextType {
  getAvatar: (
    email: string,
    auth0Picture?: string
  ) => Promise<string | undefined>;
  getCachedAvatar: (email: string) => string | undefined;
  isAvatarLoading: (email: string) => boolean;
  preloadAvatars: (emails: string[]) => void;
}

const AvatarCacheContext = createContext<AvatarCacheContextType | undefined>(
  undefined
);

const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

export const AvatarCacheProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [avatarCache, setAvatarCache] = useState<AvatarCache>({});
  const avatarCacheRef = useRef<AvatarCache>({});
  const callApi = useApi();
  const avatarRequestsRef = useRef<
    Record<string, Promise<string | undefined>>
  >({});
  const callApiRef = useRef(callApi);

  // Keep refs in sync
  avatarCacheRef.current = avatarCache;
  callApiRef.current = callApi;

  const getAvatar = useCallback(
    async (
      email: string,
      auth0Picture?: string
    ): Promise<string | undefined> => {
      if (!email) return auth0Picture;

      const now = Date.now();
      const cached = avatarCacheRef.current[email];

      // Return cached data if it's still valid
      if (
        cached &&
        now - cached.timestamp < CACHE_DURATION &&
        !cached.loading
      ) {
        return cached.avatarUrl;
      }

      const pendingRequest = avatarRequestsRef.current[email];
      if (pendingRequest) return pendingRequest;

      const request = (async (): Promise<string | undefined> => {
        // Set loading state
        setAvatarCache((prev) => ({
          ...prev,
          [email]: { ...prev[email], loading: true },
        }));

        try {
          const response = await callApiRef.current(
            `users/${email}/avatar`,
            { method: "GET" },
            "openid email profile"
          );

          // Default to auth0 picture; we'll override if we successfully parse a URL
          let avatarUrl: string | undefined = auth0Picture;

          // Server gives 204 on no avatars found
          if (response.status !== 204) {
            const contentType = response.headers.get("content-type") || "";
            try {
              if (contentType.includes("application/json")) {
                const data = await response.json();
                avatarUrl = data?.avatarUrl ?? auth0Picture;
              } else {
                // Fallback: attempt to read text and parse if present
                const body = await response.text();
                if (body && body.trim().length > 0) {
                  try {
                    const data = JSON.parse(body);
                    avatarUrl = (data && data.avatarUrl) || auth0Picture;
                  } catch {
                    // Non-JSON body; keep fallback
                  }
                }
              }
            } catch {
              // Parsing failed; keep fallback
            }
          }

          setAvatarCache((prev) => ({
            ...prev,
            [email]: {
              avatarUrl,
              timestamp: now,
              loading: false,
            },
          }));

          return avatarUrl;
        } catch (error) {
          console.error(`Error fetching avatar for ${email}:`, error);

          const fallbackUrl = auth0Picture;
          setAvatarCache((prev) => ({
            ...prev,
            [email]: {
              avatarUrl: fallbackUrl,
              timestamp: now,
              loading: false,
            },
          }));

          return fallbackUrl;
        }
      })();

      avatarRequestsRef.current[email] = request;
      try {
        return await request;
      } finally {
        if (avatarRequestsRef.current[email] === request) {
          delete avatarRequestsRef.current[email];
        }
      }
    },
    [] // Remove callApi dependency since we're using ref
  );

  const getCachedAvatar = useCallback(
    (email: string): string | undefined => {
      return avatarCacheRef.current[email]?.avatarUrl;
    },
    [] // No dependencies since we use ref
  );

  const isAvatarLoading = useCallback(
    (email: string): boolean => {
      return avatarCacheRef.current[email]?.loading || false;
    },
    [] // No dependencies since we use ref
  );

  const preloadAvatars = useCallback(
    (emails: string[]) => {
      emails.forEach((email) => {
        if (email && !avatarCacheRef.current[email]) {
          getAvatar(email);
        }
      });
    },
    [getAvatar]
  );

  const value: AvatarCacheContextType = useMemo(
    () => ({
      getAvatar,
      getCachedAvatar,
      isAvatarLoading,
      preloadAvatars,
    }),
    [getAvatar, getCachedAvatar, isAvatarLoading, preloadAvatars]
  );

  return (
    <AvatarCacheContext.Provider value={value}>
      {children}
    </AvatarCacheContext.Provider>
  );
};

export const useOptionalAvatarCache = (): AvatarCacheContextType | undefined =>
  useContext(AvatarCacheContext);

export const useAvatarCache = (): AvatarCacheContextType => {
  const context = useContext(AvatarCacheContext);
  if (context === undefined) {
    throw new Error(
      "useAvatarCache must be used within an AvatarCacheProvider"
    );
  }
  return context;
};
